// ═══════════════════════════════════════════════════════════════════
// Pointage.jsx — Éditeurs de pointages (admin) + hook géolocalisation
// Composants : PointageTcEditor, PointageAlsCreator, PointageAlsEditor,
//              AdminEditBadge, useGeo
// ═══════════════════════════════════════════════════════════════════
import React from 'react';
import { M } from './M.jsx'
import { today, now, fname, getBreaks, tsToLocal, localToTs, fileToDataUrl } from '../utils.js'

const { useState, useEffect, useMemo } = React


// Modale d'édition d'un pointage journalier (tc) — saisie de sw/breaks/ew + justification
function PointageTcEditor({record,sid,staff,initialDate,editorName,onSave,onClose}){
  const isNew=!record;
  const [date,setDate]=useState(record?.date||initialDate||today());
  const [selSid,setSelSid]=useState(record?.sid||sid||(staff?.[0]?.id||''));
  const [sw,setSw]=useState(tsToLocal(record?.sw?.ts));
  const [ew,setEw]=useState(tsToLocal(record?.ew?.ts));
  // Charger toutes les pauses existantes (legacy sb/eb + breaks[]) sous forme de liste éditable
  const [breaks,setBreaks]=useState(()=>{
    const all=getBreaks(record).map(b=>({start:tsToLocal(b.start),end:b.end?tsToLocal(b.end):'',_admin:!!b._admin}));
    return all;
  });
  // Marquages "Admin" pour début et fin de poste (modifiables manuellement)
  const [swAdmin,setSwAdmin]=useState(!!record?.sw?._admin);
  const [ewAdmin,setEwAdmin]=useState(!!record?.ew?._admin);
  const [comment,setComment]=useState(record?.editComment||'');
  const [photo,setPhoto]=useState(record?.editPhoto||null);
  const [err,setErr]=useState('');
  const onFile=async e=>{const f=e.target.files?.[0];if(!f)return;if(!f.type.startsWith('image/')){setErr('Choisissez une image.');return;}try{setPhoto(await fileToDataUrl(f));setErr('');}catch{setErr('Image illisible.');}};
  // Quand on ajoute une nouvelle pause, on pré-remplit la date + heure de midi par défaut
  // (l'utilisateur n'a plus qu'à ajuster l'heure, et la date est forcément la bonne)
  const addBreak=()=>{
    const baseDate=date||today();
    // Nouvelle pause ajoutée par l'admin → marquée Admin par défaut
    setBreaks([...breaks,{start:`${baseDate}T12:00`,end:`${baseDate}T12:30`,_admin:true}]);
  };
  const updateBreak=(i,k,v)=>setBreaks(breaks.map((b,idx)=>idx===i?{...b,[k]:v}:b));
  const removeBreak=i=>setBreaks(breaks.filter((_,idx)=>idx!==i));
  const toggleBreakAdmin=i=>setBreaks(breaks.map((b,idx)=>idx===i?{...b,_admin:!b._admin}:b));
  const save=()=>{
    if(!selSid){setErr('Choisissez un salarié.');return;}
    if(!date){setErr('Choisissez une date.');return;}
    if(!comment.trim()){setErr('Un commentaire justificatif est obligatoire.');return;}
    if(!sw){setErr('L\'heure de début est obligatoire.');return;}
    // Vérifier que les pauses sont bien à la date du pointage (sinon erreur de saisie probable)
    const expectedDate=date;
    const badBreaks=breaks.filter(b=>b.start&&b.start.slice(0,10)!==expectedDate);
    if(badBreaks.length>0){
      setErr(`Une pause a une date différente de celle du pointage (${expectedDate}). Vérifiez vos saisies.`);
      return;
    }
    const swTs=localToTs(sw);
    const ewTs=ew?localToTs(ew):null;
    // _admin sur sw/ew : utilise le state (modifiable manuellement via toggle)
    // Si la valeur a changé → forcer _admin:true. Sinon on respecte le state (qui hérite de la valeur en base).
    const swUnchanged=record?.sw&&record.sw.ts===swTs;
    const ewUnchanged=(record?.ew&&record.ew.ts===ewTs)||(!record?.ew&&!ewTs);
    const swFinalAdmin=swUnchanged?swAdmin:true;
    const ewFinalAdmin=ewUnchanged?ewAdmin:true;
    // Pour les pauses : on utilise le _admin du state (modifiable via toggle dans la modale)
    // Si la pause n'existait pas avant ou a été modifiée → forcer _admin:true via le state lors du save
    const originalBreaks=getBreaks(record);
    // getBreaks() ne rend que {start,end,_admin} : les drapeaux métier posés par l'appli
    // (_declared depuis v125, _autoClosed/_startOrig depuis v130) n'y figurent pas. Sans
    // relecture du tableau BRUT, toute édition admin d'un pointage les effaçait
    // silencieusement — dette antérieure, fermée ici.
    const rawBreaks=Array.isArray(record?.breaks)?record.breaks:[];
    const cleanBreaks=breaks.filter(b=>b.start).map(b=>{
      const startTs=localToTs(b.start);
      const endTs=b.end?localToTs(b.end):null;
      // Si la pause a changé d'horaire par rapport à l'original → forcer Admin
      const original=originalBreaks.find(o=>o.start===startTs&&(o.end||null)===endTs);
      const isAdmin=!original?true:b._admin;
      const raw=original?rawBreaks.find(o=>o?.start===startTs&&(o.end||null)===endTs):null;
      return{...(raw||{}),start:startTs,end:endTs,_admin:isAdmin};
    });
    const out={
      ...(record||{}),
      id:record?.id||'tc'+Date.now(),
      sid:selSid,
      date,
      // Si valeur inchangée : on garde geo et on utilise le _admin du state (modifiable manuellement)
      // Si modifiée : on écrit nouvelle valeur avec _admin:true
      sw:swUnchanged?{...record.sw,_admin:swFinalAdmin}:{ts:swTs,geo:record?.sw?.geo||null,_admin:true},
      sb:null,
      eb:null,
      ew:!ewTs?null:(ewUnchanged?{...record.ew,_admin:ewFinalAdmin}:{ts:ewTs,geo:record?.ew?.geo||null,_admin:true}),
      breaks:cleanBreaks,
      editedBy:editorName||'Admin',
      editedAt:now(),
      editComment:comment.trim(),
      editPhoto:photo||null,
      _adminCreated:isNew||record?._adminCreated||false,
    };
    onSave(out);
  };
  return(<div style={{display:'flex',flexDirection:'column',gap:14}}>
    {isNew&&(<div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
      <div><div style={{fontSize:11,fontWeight:700,color:'var(--si)',marginBottom:4}}>Salarié</div>
        <select value={selSid} onChange={e=>setSelSid(e.target.value)} style={{width:'100%',padding:'8px 10px',borderRadius:8,border:'1px solid var(--li)',fontSize:13}}>
          <option value="">— Choisir —</option>
          {(staff||[]).map(s=><option key={s.id} value={s.id}>{fname(s)}</option>)}
        </select></div>
      <div><div style={{fontSize:11,fontWeight:700,color:'var(--si)',marginBottom:4}}>Date</div>
        <input type="date" value={date} onChange={e=>setDate(e.target.value)} style={{width:'100%',padding:'8px 10px',borderRadius:8,border:'1px solid var(--li)',fontSize:13}}/></div>
    </div>)}
    {!isNew&&(<div style={{padding:'8px 12px',background:'#f5f5f5',borderRadius:8,fontSize:12}}>
      <strong>{fname((staff||[]).find(s=>s.id===record.sid))||'—'}</strong> · {new Date(record.date).toLocaleDateString('fr-FR',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}
    </div>)}
    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
      <div>
        <div style={{fontSize:11,fontWeight:700,color:'var(--si)',marginBottom:4}}>🟢 Début *</div>
        <input type="datetime-local" value={sw} onChange={e=>setSw(e.target.value)} style={{width:'100%',padding:'8px 10px',borderRadius:8,border:'1px solid var(--li)',fontSize:13}}/>
        <div style={{marginTop:4,fontSize:10,display:'flex',gap:5,alignItems:'center'}}>
          <span style={{color:'var(--si)'}}>Saisie :</span>
          <button onClick={()=>setSwAdmin(false)} className={swAdmin?'btn bgh':'btn bmn'} style={{fontSize:10,padding:'2px 7px',minHeight:0}}>Salarié</button>
          <button onClick={()=>setSwAdmin(true)} className={swAdmin?'btn bor':'btn bgh'} style={{fontSize:10,padding:'2px 7px',minHeight:0}}>Admin</button>
        </div>
      </div>
      <div>
        <div style={{fontSize:11,fontWeight:700,color:'var(--si)',marginBottom:4}}>🔴 Fin</div>
        <input type="datetime-local" value={ew} onChange={e=>setEw(e.target.value)} style={{width:'100%',padding:'8px 10px',borderRadius:8,border:'1px solid var(--li)',fontSize:13}}/>
        <div style={{marginTop:4,fontSize:10,display:'flex',gap:5,alignItems:'center'}}>
          <span style={{color:'var(--si)'}}>Saisie :</span>
          <button onClick={()=>setEwAdmin(false)} className={ewAdmin?'btn bgh':'btn bmn'} style={{fontSize:10,padding:'2px 7px',minHeight:0}}>Salarié</button>
          <button onClick={()=>setEwAdmin(true)} className={ewAdmin?'btn bor':'btn bgh'} style={{fontSize:10,padding:'2px 7px',minHeight:0}}>Admin</button>
        </div>
      </div>
    </div>
    <div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6}}>
        <div style={{fontSize:11,fontWeight:700,color:'var(--si)'}}>☕ Pauses</div>
        <button onClick={addBreak} className="btn bgh sm" style={{fontSize:11,padding:'3px 8px'}}>+ Ajouter une pause</button>
      </div>
      {breaks.length===0?<div style={{fontSize:12,color:'var(--si)',fontStyle:'italic',padding:'6px 0'}}>Aucune pause. Cliquez sur "+ Ajouter une pause" si nécessaire.</div>:(
        <div style={{display:'flex',flexDirection:'column',gap:6}}>
          {breaks.map((b,i)=>(<div key={i} style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap',padding:'4px 6px',background:'var(--bg)',borderRadius:8}}>
            <span style={{fontSize:11,minWidth:24,fontWeight:700,color:'var(--si)'}}>#{i+1}</span>
            <input type="datetime-local" value={b.start} onChange={e=>updateBreak(i,'start',e.target.value)} placeholder="Début" style={{flex:'1 1 140px',padding:'6px 8px',borderRadius:8,border:'1px solid var(--li)',fontSize:12}}/>
            <span style={{fontSize:11,color:'var(--si)'}}>→</span>
            <input type="datetime-local" value={b.end} onChange={e=>updateBreak(i,'end',e.target.value)} placeholder="Fin" style={{flex:'1 1 140px',padding:'6px 8px',borderRadius:8,border:'1px solid var(--li)',fontSize:12}}/>
            <div style={{display:'flex',gap:3,alignItems:'center'}}>
              <button onClick={()=>setBreaks(breaks.map((x,idx)=>idx===i?{...x,_admin:false}:x))} className={b._admin?'btn bgh':'btn bmn'} style={{fontSize:10,padding:'2px 6px',minHeight:0}} title="Pointage du salarié">Sal.</button>
              <button onClick={()=>setBreaks(breaks.map((x,idx)=>idx===i?{...x,_admin:true}:x))} className={b._admin?'btn bor':'btn bgh'} style={{fontSize:10,padding:'2px 6px',minHeight:0}} title="Saisi par admin">Adm.</button>
            </div>
            <button onClick={()=>removeBreak(i)} className="btn bdg sm" style={{padding:'3px 7px',fontSize:11}}>✕</button>
          </div>))}
        </div>
      )}
    </div>
    <div><div style={{fontSize:11,fontWeight:700,color:'var(--si)',marginBottom:4}}>💬 Justification (obligatoire)</div>
      <textarea value={comment} onChange={e=>setComment(e.target.value)} rows={3} placeholder="Ex: oubli de dépointage, le salarié a confirmé l'heure de fin par SMS" style={{width:'100%',padding:'8px 10px',borderRadius:8,border:'1px solid var(--li)',fontSize:13,fontFamily:'inherit',resize:'vertical'}}/></div>
    <div><div style={{fontSize:11,fontWeight:700,color:'var(--si)',marginBottom:4}}>📷 Photo justificative (facultative)</div>
      <input type="file" accept="image/*" onChange={onFile} style={{fontSize:12}}/>
      {photo&&(<div style={{marginTop:8,position:'relative',display:'inline-block'}}>
        <img src={photo} alt="justif" style={{maxWidth:200,maxHeight:200,borderRadius:8,border:'1px solid var(--li)'}}/>
        <button onClick={()=>setPhoto(null)} className="btn bdg sm" style={{position:'absolute',top:4,right:4,padding:'3px 7px'}}>✕</button>
      </div>)}</div>
    {err&&<div style={{padding:'8px 12px',background:'#fde0e0',color:'var(--dg)',borderRadius:8,fontSize:12,fontWeight:600}}>⚠ {err}</div>}
    <div style={{display:'flex',gap:10,justifyContent:'flex-end',paddingTop:6}}>
      <button onClick={onClose} className="btn bgh">Annuler</button>
      <button onClick={save} className="btn bor">✓ {isNew?'Créer':'Enregistrer'}</button>
    </div>
  </div>);
}

// Modale d'édition d'un pointage d'appartement (als) — saisie ts + justification
// Création d'un pointage d'appartement par l'admin (ajout manuel — typiquement quand un salarié
// a oublié de pointer la fin d'un ménage). Auto-lie au start ouvert le plus récent si type=end.
function PointageAlsCreator({biens,staff,gerants,als,editorName,onSave,onClose,prefilledSid,prefilledBid,prefilledType}){
  const allWorkers=useMemo(()=>{
    const a=(staff||[]).filter(s=>!s.archived).map(s=>({id:s.id,label:fname(s),isGerant:false}));
    const b=(gerants||[]).filter(g=>!g.archived).map(g=>({id:g.id,label:g.name+' (direction)',isGerant:true}));
    return[...a,...b];
  },[staff,gerants]);
  const[sid,setSid]=useState(prefilledSid||allWorkers[0]?.id||'');
  const[bid,setBid]=useState(prefilledBid||biens[0]?.id||'');
  const[type,setType]=useState(prefilledType||'end');
  const[ts,setTs]=useState(tsToLocal(now()));
  const[comment,setComment]=useState('');
  const[photo,setPhoto]=useState(null);
  const[err,setErr]=useState('');
  // Pour un "end" : montrer les "start" ouverts du salarié+bien (= start sans end correspondant) sur les 7 derniers jours
  const openStarts=useMemo(()=>{
    if(type!=='end'||!sid||!bid)return[];
    const cutoff=Date.now()-7*24*3600*1000;
    return(als||[]).filter(l=>l.sid===sid&&l.bid===bid&&l.type==='start'&&new Date(l.ts).getTime()>cutoff)
      .filter(s=>!(als||[]).some(e=>e.tid===s.tid&&e.type==='end'))
      .sort((a,b)=>new Date(b.ts)-new Date(a.ts));
  },[type,sid,bid,als]);
  const[linkedStartId,setLinkedStartId]=useState('');
  // Auto-sélection du start le plus récent si type passe à "end"
  useEffect(()=>{
    if(type==='end'&&openStarts.length>0&&!linkedStartId){setLinkedStartId(openStarts[0].id);}
    if(type==='start'){setLinkedStartId('');}
  },[type,openStarts.length]);
  const onFile=async e=>{const f=e.target.files?.[0];if(!f)return;if(!f.type.startsWith('image/')){setErr('Choisissez une image.');return;}try{setPhoto(await fileToDataUrl(f));setErr('');}catch{setErr('Image illisible.');}};
  const save=()=>{
    if(!sid){setErr('Sélectionnez un salarié.');return;}
    if(!bid){setErr('Sélectionnez un appartement.');return;}
    if(!ts){setErr('Choisissez une date et une heure.');return;}
    if(!comment.trim()){setErr('Un commentaire justificatif est obligatoire.');return;}
    // Déterminer le tid : (a) lié à un start existant si end+choisi, (b) sinon nouveau
    let tid;
    if(type==='end'&&linkedStartId){
      const startRec=(als||[]).find(l=>l.id===linkedStartId);
      tid=startRec?.tid||('t'+Date.now());
    }else{
      tid='t'+Date.now();
    }
    const newRec={
      id:'a'+Date.now(),
      sid,bid,tid,type,
      ts:localToTs(ts),
      geo:null,geoErr:null,ok:null,dist:null,
      // Marqueurs admin
      editedBy:editorName||'Admin',editedAt:now(),
      editComment:comment.trim(),editPhoto:photo||null,
      _adminCreated:true
    };
    onSave(newRec);
  };
  return(<div style={{display:'flex',flexDirection:'column',gap:14}}>
    <div style={{padding:'10px 14px',background:'#fff4d6',borderRadius:8,fontSize:12,borderLeft:'3px solid var(--or)'}}>
      ⚠️ Vous créez un pointage manuellement. Une trace "Ajouté par {editorName||'Admin'}" sera conservée sur le pointage.
    </div>
    <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
      <div style={{flex:'1 1 200px'}}>
        <div style={{fontSize:11,fontWeight:700,color:'var(--si)',marginBottom:4}}>Type</div>
        <select value={type} onChange={e=>setType(e.target.value)} style={{width:'100%',padding:'8px 10px',borderRadius:8,border:'1px solid var(--li)',fontSize:13}}>
          <option value="start">▶ Début</option>
          <option value="end">⏹ Fin</option>
        </select>
      </div>
      <div style={{flex:'1 1 200px'}}>
        <div style={{fontSize:11,fontWeight:700,color:'var(--si)',marginBottom:4}}>Salarié / Direction</div>
        <select value={sid} onChange={e=>setSid(e.target.value)} style={{width:'100%',padding:'8px 10px',borderRadius:8,border:'1px solid var(--li)',fontSize:13}}>
          {allWorkers.map(w=><option key={w.id} value={w.id}>{w.label}</option>)}
        </select>
      </div>
    </div>
    <div>
      <div style={{fontSize:11,fontWeight:700,color:'var(--si)',marginBottom:4}}>Appartement</div>
      <select value={bid} onChange={e=>setBid(e.target.value)} style={{width:'100%',padding:'8px 10px',borderRadius:8,border:'1px solid var(--li)',fontSize:13}}>
        {biens.filter(b=>!b.archived).map(b=><option key={b.id} value={b.id}>{b.name}</option>)}
      </select>
    </div>
    {type==='end'&&openStarts.length>0&&<div style={{padding:'8px 12px',background:'#e1f7ee',borderRadius:8,fontSize:12}}>
      <div style={{fontWeight:700,marginBottom:4,color:'var(--ok)'}}>🔗 Lier ce "Fin" à un "Début" ouvert ?</div>
      <select value={linkedStartId} onChange={e=>setLinkedStartId(e.target.value)} style={{width:'100%',padding:'6px 8px',borderRadius:6,border:'1px solid var(--li)',fontSize:12}}>
        <option value="">— Aucun (créer indépendamment) —</option>
        {openStarts.map(s=><option key={s.id} value={s.id}>Début du {new Date(s.ts).toLocaleString('fr-FR',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'})}</option>)}
      </select>
    </div>}
    {type==='end'&&openStarts.length===0&&<div style={{padding:'8px 12px',background:'#fde0e0',color:'#8a4000',borderRadius:8,fontSize:12}}>
      ℹ️ Aucun "Début" ouvert récent pour ce salarié + appartement. Le pointage de fin sera créé sans rattachement (durée non calculable).
    </div>}
    <div>
      <div style={{fontSize:11,fontWeight:700,color:'var(--si)',marginBottom:4}}>Horodatage</div>
      <input type="datetime-local" value={ts} onChange={e=>setTs(e.target.value)} style={{width:'100%',padding:'8px 10px',borderRadius:8,border:'1px solid var(--li)',fontSize:13}}/>
    </div>
    <div>
      <div style={{fontSize:11,fontWeight:700,color:'var(--si)',marginBottom:4}}>💬 Justification (obligatoire)</div>
      <textarea value={comment} onChange={e=>setComment(e.target.value)} rows={3} placeholder="Ex: salarié a oublié de pointer la fin du ménage, horaire confirmé verbalement" style={{width:'100%',padding:'8px 10px',borderRadius:8,border:'1px solid var(--li)',fontSize:13,fontFamily:'inherit',resize:'vertical'}}/>
    </div>
    <div>
      <div style={{fontSize:11,fontWeight:700,color:'var(--si)',marginBottom:4}}>📷 Photo justificative (facultative)</div>
      <input type="file" accept="image/*" onChange={onFile} style={{fontSize:12}}/>
      {photo&&(<div style={{marginTop:8,position:'relative',display:'inline-block'}}>
        <img src={photo} alt="justif" style={{maxWidth:200,maxHeight:200,borderRadius:8,border:'1px solid var(--li)'}}/>
        <button onClick={()=>setPhoto(null)} className="btn bdg sm" style={{position:'absolute',top:4,right:4,padding:'3px 7px'}}>✕</button>
      </div>)}
    </div>
    {err&&<div style={{padding:'8px 12px',background:'#fde0e0',color:'var(--dg)',borderRadius:8,fontSize:12,fontWeight:600}}>⚠ {err}</div>}
    <div style={{display:'flex',gap:10,justifyContent:'flex-end',paddingTop:6}}>
      <button onClick={onClose} className="btn bgh">Annuler</button>
      <button onClick={save} className="btn bor">⊕ Créer le pointage</button>
    </div>
  </div>);
}

function PointageAlsEditor({record,biens,editorName,onSave,onClose}){
  const [ts,setTs]=useState(tsToLocal(record?.ts));
  const [comment,setComment]=useState(record?.editComment||'');
  const [photo,setPhoto]=useState(record?.editPhoto||null);
  const [err,setErr]=useState('');
  const b=biens.find(x=>x.id===record?.bid);
  const onFile=async e=>{const f=e.target.files?.[0];if(!f)return;if(!f.type.startsWith('image/')){setErr('Choisissez une image.');return;}try{setPhoto(await fileToDataUrl(f));setErr('');}catch{setErr('Image illisible.');}};
  const save=()=>{
    if(!ts){setErr('Choisissez une heure.');return;}
    if(!comment.trim()){setErr('Un commentaire justificatif est obligatoire.');return;}
    onSave({...record,ts:localToTs(ts),editedBy:editorName||'Admin',editedAt:now(),editComment:comment.trim(),editPhoto:photo||null});
  };
  return(<div style={{display:'flex',flexDirection:'column',gap:14}}>
    <div style={{padding:'8px 12px',background:'#f5f5f5',borderRadius:8,fontSize:12}}>
      <strong>{record?.type==='start'?'▶ Début':'⏹ Fin'}</strong> · {b?.name||'—'}
    </div>
    <div><div style={{fontSize:11,fontWeight:700,color:'var(--si)',marginBottom:4}}>Horodatage</div>
      <input type="datetime-local" value={ts} onChange={e=>setTs(e.target.value)} style={{width:'100%',padding:'8px 10px',borderRadius:8,border:'1px solid var(--li)',fontSize:13}}/></div>
    <div><div style={{fontSize:11,fontWeight:700,color:'var(--si)',marginBottom:4}}>💬 Justification (obligatoire)</div>
      <textarea value={comment} onChange={e=>setComment(e.target.value)} rows={3} placeholder="Ex: salarié a oublié de pointer l'arrivée, horaire confirmé par téléphone" style={{width:'100%',padding:'8px 10px',borderRadius:8,border:'1px solid var(--li)',fontSize:13,fontFamily:'inherit',resize:'vertical'}}/></div>
    <div><div style={{fontSize:11,fontWeight:700,color:'var(--si)',marginBottom:4}}>📷 Photo justificative (facultative)</div>
      <input type="file" accept="image/*" onChange={onFile} style={{fontSize:12}}/>
      {photo&&(<div style={{marginTop:8,position:'relative',display:'inline-block'}}>
        <img src={photo} alt="justif" style={{maxWidth:200,maxHeight:200,borderRadius:8,border:'1px solid var(--li)'}}/>
        <button onClick={()=>setPhoto(null)} className="btn bdg sm" style={{position:'absolute',top:4,right:4,padding:'3px 7px'}}>✕</button>
      </div>)}</div>
    {err&&<div style={{padding:'8px 12px',background:'#fde0e0',color:'var(--dg)',borderRadius:8,fontSize:12,fontWeight:600}}>⚠ {err}</div>}
    <div style={{display:'flex',gap:10,justifyContent:'flex-end',paddingTop:6}}>
      <button onClick={onClose} className="btn bgh">Annuler</button>
      <button onClick={save} className="btn bor">✓ Enregistrer</button>
    </div>
  </div>);
}

// Badge "modifié/ajouté par admin" + bulle de justification au clic
function AdminEditBadge({record,size='sm'}){
  const [open,setOpen]=useState(false);
  if(!record?.editedBy)return null;
  const isCreated=record._adminCreated;
  return(<>
    <button onClick={e=>{e.stopPropagation();setOpen(true);}} title={isCreated?'Ajouté par '+record.editedBy:'Modifié par '+record.editedBy} style={{background:isCreated?'#fff4d6':'#e8f0ff',color:isCreated?'#7a5a00':'#1a4a8a',border:`1px solid ${isCreated?'#e5c977':'#b8cfee'}`,fontSize:size==='sm'?9:11,fontWeight:700,padding:size==='sm'?'2px 6px':'3px 8px',borderRadius:999,cursor:'pointer',whiteSpace:'nowrap'}}>
      {isCreated?'⊕':'✎'} {isCreated?'Ajouté':'Modifié'}
    </button>
    {open&&<M onC={()=>setOpen(false)} title={isCreated?'Pointage ajouté par admin':'Pointage modifié par admin'} col="var(--or)" ch={
      <div style={{display:'flex',flexDirection:'column',gap:10}}>
        <div style={{fontSize:12,color:'var(--si)'}}>Par <strong style={{color:'var(--ink)'}}>{record.editedBy}</strong> · {record.editedAt?new Date(record.editedAt).toLocaleString('fr-FR'):'—'}</div>
        <div style={{padding:'10px 14px',background:'#fff8e1',borderRadius:8,fontSize:13,borderLeft:'3px solid var(--or)'}}>{record.editComment||'—'}</div>
        {record.editPhoto&&<img src={record.editPhoto} alt="justif" style={{maxWidth:'100%',borderRadius:8,border:'1px solid var(--li)'}}/>}
      </div>
    }/>}
  </>);
}


// ══════════════ GEO ══════════════
export function useGeo() {
  const [st, setSt] = useState('idle')
  const req = cb => {
    setSt('pending')
    if (!navigator.geolocation) { setSt('err'); cb(null, 'Non supporté'); return }
    navigator.geolocation.getCurrentPosition(
      p => { const g = { lat: p.coords.latitude, lng: p.coords.longitude, acc: Math.round(p.coords.accuracy), ts: now() }; setSt('ok'); cb(g, null) },
      e => { setSt('err'); cb(null, e.message) },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    )
  }
  return { st, req }
}

export { PointageTcEditor, PointageAlsCreator, PointageAlsEditor, AdminEditBadge }
